import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Pagination } from 'react-bootstrap';
import clinicalReportsService from '../../../services/clinicalReportsService';
import Icon from '../../common/Icons';
import StatisticalDataTable from './StatisticalDataTable';

function formatReportDateTime(value) {
  if (!value) return '—';
  const raw = String(value).replace('T', ' ').trim();
  const datePart = raw.slice(0, 10);
  const timePart = raw.length >= 16 ? raw.slice(11, 16) : '';
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return raw;
  return timePart ? `${day}/${month}/${year} ${timePart}` : `${day}/${month}/${year}`;
}

function formatAge(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

/**
 * Journal des actes infirmiers — même tableau que le rapport major (synthèse).
 */
export default function NursingActsSynthesisPanel({
  reportFilters,
  copy,
  hasActiveAdvancedFilters = false,
  onClearFilters = null,
  showSectionHead = false,
  sectionTitle = '',
  sectionDescription = ''
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    total_pages: 1,
    has_prev: false,
    has_next: false
  });

  useEffect(() => {
    setPage(1);
  }, [reportFilters]);

  const load = useCallback(async () => {
    if (!reportFilters) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await clinicalReportsService.getNursingActsSynthesis({
        ...reportFilters,
        page,
        limit: pageSize
      });
      if (res.success) {
        const nextRows = (res.data?.rows || []).map((row, idx) => ({
          ...row,
          id: `${page}-${idx}-${row.date}-${row.matricule}-${row.acte}-${row.injection}-${row.planning_familial}`
        }));
        setRows(nextRows);
        setTotal(res.data?.total ?? nextRows.length);
        setPagination(
          res.data?.pagination || {
            page,
            limit: pageSize,
            total: res.data?.total ?? nextRows.length,
            total_pages: 1,
            has_prev: false,
            has_next: false
          }
        );
      } else {
        setRows([]);
        setTotal(0);
        setPagination({
          page,
          limit: pageSize,
          total: 0,
          total_pages: 1,
          has_prev: false,
          has_next: false
        });
        setError(res.message || copy.ERROR_SYNTHESIS_LOAD);
      }
    } catch (err) {
      setRows([]);
      setTotal(0);
      setPagination({
        page,
        limit: pageSize,
        total: 0,
        total_pages: 1,
        has_prev: false,
        has_next: false
      });
      setError(err?.message || copy.ERROR_SYNTHESIS_LOAD);
    } finally {
      setLoading(false);
    }
  }, [reportFilters, page, pageSize, copy.ERROR_SYNTHESIS_LOAD]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        key: 'date',
        label: copy.COL_DATE,
        sortAccessor: (row) => row.date_time || row.date,
        render: (row) => formatReportDateTime(row.date_time || row.date)
      },
      { key: 'nom', label: copy.COL_NOM, sortAccessor: (row) => row.nom },
      { key: 'prenom', label: copy.COL_PRENOM, sortAccessor: (row) => row.prenom },
      { key: 'societe', label: copy.COL_SOCIETE, sortAccessor: (row) => row.societe },
      { key: 'matricule', label: copy.COL_MATRICULE, sortAccessor: (row) => row.matricule },
      { key: 'type', label: copy.COL_TYPE, sortAccessor: (row) => row.type },
      { key: 'sexe', label: copy.COL_SEXE, sortAccessor: (row) => row.sexe },
      {
        key: 'age',
        label: copy.COL_AGE,
        className: 'text-end',
        sortAccessor: (row) => row.age,
        render: (row) => formatAge(row.age)
      },
      { key: 'acte', label: copy.COL_ACTE, sortAccessor: (row) => row.acte },
      { key: 'injection', label: copy.COL_INJECTION, sortAccessor: (row) => row.injection },
      {
        key: 'planning_familial',
        label: copy.COL_PLANNING_FAMILIAL,
        sortAccessor: (row) => row.planning_familial
      }
    ],
    [copy]
  );

  const handleExport = async () => {
    if (!reportFilters || exporting) return;
    setExporting(true);
    try {
      await clinicalReportsService.downloadNursingActsCsv({
        ...reportFilters,
        limit: 10000
      });
    } catch (err) {
      setError(err.message || copy.ERROR_SYNTHESIS_LOAD);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="hd-nursing-synthesis mb-4" aria-label={sectionTitle || copy.PAGE_OVERVIEW_TITLE}>
      {showSectionHead ? (
        <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-2 mb-3">
          <div>
            <h3 className="hd-reports-table-title mb-1">{sectionTitle || copy.PAGE_OVERVIEW_TITLE}</h3>
            {sectionDescription || copy.PAGE_OVERVIEW_DESC ? (
              <p className="hd-reports-section-desc mb-0">
                {sectionDescription || copy.PAGE_OVERVIEW_DESC}
              </p>
            ) : null}
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={loading || exporting || !reportFilters}
            onClick={handleExport}
          >
            <Icon name="download" size={14} className="me-1" aria-hidden />
            {exporting ? copy.SYNTHESIS_EXPORTING : copy.SYNTHESIS_EXPORT}
          </Button>
        </div>
      ) : (
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="outline-primary"
            size="sm"
            disabled={loading || exporting || !reportFilters}
            onClick={handleExport}
          >
            <Icon name="download" size={14} className="me-1" aria-hidden />
            {exporting ? copy.SYNTHESIS_EXPORTING : copy.SYNTHESIS_EXPORT}
          </Button>
        </div>
      )}

      {error ? (
        <div className="hd-reports-panel mb-3">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <p className="text-danger mb-0">{error}</p>
            <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
              <Icon name="refresh" size={14} className="me-1" aria-hidden />
              {copy.RETRY_LABEL}
            </Button>
          </div>
        </div>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3">
          <p className="hd-reports-section-desc mb-0">{copy.SYNTHESIS_COUNT(total)}</p>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{copy.SYNTHESIS_PER_PAGE}</span>
            <Form.Select
              size="sm"
              style={{ width: 'auto' }}
              value={String(pageSize)}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(1);
              }}
              disabled={loading}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Form.Select>
          </div>
        </div>
      ) : null}

      {!loading && !error && rows.length === 0 ? (
        <div className="hd-reports-panel mb-3">
          <p className="mb-1">{copy.EMPTY_SYNTHESIS}</p>
          <p className="text-muted mb-0">{copy.EMPTY_SYNTHESIS_HINT}</p>
          {hasActiveAdvancedFilters && onClearFilters ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="mt-2"
              onClick={onClearFilters}
              disabled={loading}
            >
              <Icon name="filter" size={14} className="me-1" aria-hidden />
              {copy.EMPTY_CLEAR_FILTERS}
            </Button>
          ) : null}
        </div>
      ) : null}

      <StatisticalDataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        emptyLabel={copy.EMPTY_SYNTHESIS}
        loadingLabel={copy.LOADING}
        tableClassName="major-reports-table"
        enableSmartSort
        defaultSort={{ key: 'date', dir: 'desc' }}
      />

      {!loading && total > 0 ? (
        <div className="hd-reports-panel mt-3 p-3">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <p className="text-muted small mb-0">
              {copy.SYNTHESIS_PAGE_STATUS(pagination.page, pagination.total_pages, pagination.total)}
            </p>
            <Pagination className="mb-0">
              <Pagination.First onClick={() => setPage(1)} disabled={!pagination.has_prev || loading} />
              <Pagination.Prev
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!pagination.has_prev || loading}
              />
              <Pagination.Item active>{pagination.page}</Pagination.Item>
              <Pagination.Next
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!pagination.has_next || loading}
              />
              <Pagination.Last
                onClick={() => setPage(pagination.total_pages)}
                disabled={!pagination.has_next || loading}
              />
            </Pagination>
          </div>
        </div>
      ) : null}
    </section>
  );
}
